import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import styled, { useTheme } from 'styled-components'
import { rem } from 'polished'

export interface TracePoint {
  x: number
  y: number
}

interface Props {
  /** Viewport coordinates the comet launches from (the Ask AI trigger). */
  origin: TracePoint
  /** Resolves the viewport coordinates the comet lands on (the context chip). Measured when the flight starts. */
  getTarget: () => TracePoint | null
  /** Called once the flight (and landing burst) has finished, so the overlay can unmount. */
  onDone: () => void
}

const FLIGHT_MS = 720
const LANDING_FADE_MS = 360

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 1200;
`

const Orb = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: ${rem(14)};
  height: ${rem(14)};
  border-radius: 50%;
  opacity: 0;
  transform: translate(-50%, -50%) scale(0);
  background: radial-gradient(
    circle at 35% 30%,
    #ffffff,
    ${props => props.theme.palette.product.ai.light} 42%,
    ${props => props.theme.palette.product.ai.dark} 82%
  );
  box-shadow:
    0 0 ${rem(10)} ${rem(3)} ${props => props.theme.palette.product.ai.light35},
    0 0 ${rem(24)} ${rem(8)} ${props => props.theme.palette.product.ai.dark25};
  will-change: transform, left, top;
`

const easeInOut = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2)

/**
 * A short, purely-decorative "comet" that flies from the Ask AI trigger across the
 * screen and lands on the chat context chip, guiding the user's eye to the context
 * that was just added. Portals to the document body and unmounts itself when done.
 * Respects prefers-reduced-motion by skipping straight to onDone.
 */
function AiContextTrace({ origin, getTarget, onDone }: Props) {
  const theme = useTheme()
  const overlayRef = useRef<HTMLDivElement>(null)
  const orbRef = useRef<HTMLDivElement>(null)

  // The flight runs once for the lifetime of this overlay instance (a fresh
  // instance is mounted via `key` for each click), so the effect deps stay
  // empty. Inputs are read through refs kept current each render so the effect
  // never goes stale without needing to re-run.
  const originRef = useRef(origin)
  originRef.current = origin
  const getTargetRef = useRef(getTarget)
  getTargetRef.current = getTarget
  const onDoneRef = useRef(onDone)
  onDoneRef.current = onDone
  const sparkColorRef = useRef(theme.palette.product.ai.light)
  sparkColorRef.current = theme.palette.product.ai.light

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    const target = getTargetRef.current()
    const orb = orbRef.current
    const overlay = overlayRef.current

    if (prefersReducedMotion || !target || !orb || !overlay) {
      onDoneRef.current()
      return
    }

    const from = originRef.current
    const to = target

    // Quadratic arc bowed up-and-out for a graceful sweep.
    const cx = from.x + (to.x - from.x) * 0.42
    const cy = Math.min(from.y, to.y) - 120
    const quad = (t: number) => ({
      x: (1 - t) * (1 - t) * from.x + 2 * (1 - t) * t * cx + t * t * to.x,
      y: (1 - t) * (1 - t) * from.y + 2 * (1 - t) * t * cy + t * t * to.y,
    })

    const spark = (x: number, y: number) => {
      const s = document.createElement('div')
      s.style.cssText =
        `position:absolute;width:${rem(5)};height:${rem(5)};border-radius:50%;` +
        `left:${x}px;top:${y}px;background:${sparkColorRef.current};` +
        'transform:translate(-50%,-50%);pointer-events:none;'
      overlay.appendChild(s)
      const angle = Math.random() * Math.PI * 2
      const dist = 6 + Math.random() * 16
      s.animate(
        [
          { transform: 'translate(-50%,-50%) translate(0,0)', opacity: 0.9 },
          {
            transform: `translate(-50%,-50%) translate(${Math.cos(angle) * dist}px,${Math.sin(angle) * dist}px)`,
            opacity: 0,
          },
        ],
        { duration: 420 + Math.random() * 220, easing: 'cubic-bezier(.2,.7,.3,1)' },
      ).onfinish = () => s.remove()
    }

    let raf = 0
    let startTs = 0
    let cancelled = false
    let doneTimer = 0

    const frame = (ts: number) => {
      if (cancelled) return
      if (!startTs) startTs = ts
      const raw = Math.min(1, (ts - startTs) / FLIGHT_MS)
      const t = easeInOut(raw)
      const p = quad(t)

      orb.style.left = `${p.x}px`
      orb.style.top = `${p.y}px`
      const scale = raw < 0.12 ? raw / 0.12 : raw > 0.9 ? 1 + (raw - 0.9) * 1.6 : 1
      orb.style.transform = `translate(-50%,-50%) scale(${scale})`
      orb.style.opacity = '1'

      if (raw < 0.95 && Math.random() < 0.6) spark(p.x, p.y)

      if (raw < 1) {
        raf = requestAnimationFrame(frame)
        return
      }

      // Landing: small burst, then collapse the orb into the chip.
      for (let i = 0; i < 10; i++) spark(p.x, p.y)
      orb.style.transition = 'transform .2s cubic-bezier(.3,.9,.2,1), opacity .2s ease'
      orb.style.transform = 'translate(-50%,-50%) scale(0)'
      orb.style.opacity = '0'
      doneTimer = window.setTimeout(() => {
        if (!cancelled) onDoneRef.current()
      }, LANDING_FADE_MS)
    }

    raf = requestAnimationFrame(frame)

    return () => {
      cancelled = true
      cancelAnimationFrame(raf)
      window.clearTimeout(doneTimer)
    }
  }, [])

  return createPortal(
    <Overlay ref={overlayRef} aria-hidden="true">
      <Orb ref={orbRef} />
    </Overlay>,
    document.body,
  )
}

export default AiContextTrace
